import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import queryKeys from "../../configs/react-query/query-key";
import { useMutationData } from "../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { AdjustIcon } from "../Icons";
import AdvancedSearch from "../Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "../Tables/TableWithPagination";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import useEscapeKey from "@hooks/useEscapeKey";

const INITIAL_LICENSE_SEARCH_CRITERIA = {
  licenseIdentifier: "",
  licenseName: "",
  defaultFlag: "",
  licenseNameForSearch: "",
};

const LicenseSelectionModal = ({ isOpen, onClose, onSelect, selectedLicense = null, "data-testid": dataTestId = "license-selection-modal" }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [licenseList, setLicenseList] = useState([]);
  const [totalLicenseData, setTotalLicenseData] = useState(0);
  const [totalLicensePages, setTotalLicensePages] = useState(0);
  const [searchLicenseCriteria, setSearchLicenseCriteria] = useState(INITIAL_LICENSE_SEARCH_CRITERIA);
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchLicenseCriteria);
  const [currentLicensePage, setCurrentLicensePage] = useState(1);
  const [tempSelectedLicenseSeq, setTempSelectedLicenseSeq] = useState([]);
  const [selectedLicenseItem, setSelectedLicenseItem] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.LICENSE_SELECTION_MODAL, DEFAULT_ITEMS_PER_PAGE);
  const isInitialMount = useRef(true);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentLicensePage(1); // Reset to page 1 when changing number of rows
  };

  // Constants cho Advanced Search
  const advancedSearchFields = [
    {
      name: "licenseIdentifier",
      label: t("project.license-identifier"),
      placeholder: t("project.license-identifier"),
      type: "input",
      keepRawLabel: true,
    },
    {
      name: "licenseName",
      label: t("project.license-name"),
      placeholder: t("project.license-name"),
      type: "input",
      keepRawLabel: true,
    },
    {
      name: "defaultFlag",
      type: "select",
      placeholder: t("project.default-flag"),
      options: [
        { value: "Y", label: t("common.yes") },
        { value: "N", label: t("common.no") },
      ],
    },
  ];

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "licenseNameForSearch",
    setSearchCriteria: setSearchLicenseCriteria,
    initialCriteria: INITIAL_LICENSE_SEARCH_CRITERIA,
  });

  // Call API to get license data
  const getLicenseListData = useMutationData(
    queryKeys.getListLicenseAttributes,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setLicenseList(response.object);
          setTotalLicenseData(response.objectCount);
          setTotalLicensePages(Math.ceil(response.objectCount / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const fetchLicenseData = (data) => {
    const effectiveData = resolveSearchCriteria(data, {
      quickSearchField: "licenseNameForSearch",
      isAdvancedPanelOpen: showAdvancedSearch,
    });
    const { licenseNameForSearch, licenseName, ...rest } = effectiveData;
    const bodyRequest = Object.keys(rest).reduce((acc, key) => {
      const value = rest[key];
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (licenseName) {
      bodyRequest.licenseName = licenseName;
    } else if (licenseNameForSearch) {
      bodyRequest.licenseName = licenseNameForSearch;
    }

    bodyRequest.pageSize = itemsPerPage;
    bodyRequest.startRow = itemsPerPage * (currentLicensePage - 1);
    getLicenseListData.mutate(bodyRequest);
  };

  // -----------------------------------------------------------------------------
  // EFFECT 1: Handle open and close modal
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      // Set flag to indicate that the modal has been mounted, so we can skip the child effects
      isInitialMount.current = true;

      // Reset all state to default
      setShowAdvancedSearch(false);
      setCurrentLicensePage(1);
      const initialCriteria = {
        licenseIdentifier: "",
        licenseName: "",
        defaultFlag: "",
      };
      setSearchLicenseCriteria(initialCriteria);
      setDebouncedSearchCriteria(initialCriteria); // Sync debounced state

      if (selectedLicense && selectedLicense.licenseIdentifier) {
        setTempSelectedLicenseSeq([selectedLicense.licenseIdentifier]);
        setSelectedLicenseItem(selectedLicense);
      } else {
        setTempSelectedLicenseSeq([]);
        setSelectedLicenseItem(null);
      }

      getLicenseListData.mutate({
        pageSize: itemsPerPage,
        startRow: 0,
      });
    } else {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setCurrentLicensePage(1);
    }
  }, [isOpen, selectedLicense]);


  // -----------------------------------------------------------------------------
  // EFFECT 2: Handle debounce when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    // Skip first run when search criteria is reset when opening modal
    if (isInitialMount.current) {
      return;
    }

    setIsSearching(true);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // When user types search, always reset to page 1
      if (currentLicensePage !== 1) {
        setCurrentLicensePage(1);
      }
      setDebouncedSearchCriteria(searchLicenseCriteria);
      setIsSearching(false);
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchLicenseCriteria]);


  // -----------------------------------------------------------------------------
  // EFFECT 3: Handle call API when user types search
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    // Skip first run when component mounts
    // isInitialMount.current will be set to false after the first run
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Call API for subsequent interactions
    fetchLicenseData(debouncedSearchCriteria);

  }, [debouncedSearchCriteria, currentLicensePage, itemsPerPage, showAdvancedSearch]);

  const licenseTableColumns = ["licenseIdentifier", "licenseName", "defaultFlag"];
  const licenseTableColumnConfig = {
    licenseIdentifier: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        textAlign: "left",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.license-identifier"),
      customRender: (value, row) => <div>{row.licenseIdentifier}</div>,
    },
    licenseName: {
      style: {
        width: "50%",
        whiteSpace: "normal",
        textAlign: "left",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.license-name"),
      customRender: (value, row) => <div>{row.licenseName}</div>,
    },
    defaultFlag: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        textAlign: "center",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.default-flag"),
      customRender: (value, row) => <div>{row.defaultFlag === "Y" ? "Yes" : "No"}</div>,
    },
  };

  const handleCancel = () => {
    // Reset selection when cancel
    setTempSelectedLicenseSeq([]);
    setSelectedLicenseItem(null);
    // Clear timeout to avoid memory leak
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };

  const handleAdvancedSearch = (searchData) => {
    setSearchLicenseCriteria((prev) => ({
      ...prev,
      ...searchData,
    }));
  };

  const handleSelect = () => {
    // Call onSelect with selected item (could be null if cleared)
    onSelect(selectedLicenseItem);
    // Clear timeout to avoid memory leak
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onClose();
  };

  const handleOnRowLicenseListClick = (data) => {
    setTempSelectedLicenseSeq([data.licenseIdentifier]);
    setSelectedLicenseItem(data); // Save the entire object
  };

  const handleOnCheckRadioChange = (data) => {
    setTempSelectedLicenseSeq(data);
    // Find object corresponding to selected ID
    if (data && data.length > 0) {
      const selectedItem = licenseList.find((item) => item.licenseIdentifier === data[0]);
      if (selectedItem) {
        setSelectedLicenseItem(selectedItem);
      } else {
        setSelectedLicenseItem(null);
      }
    } else {
      setSelectedLicenseItem(null);
    }
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div className={`${colors.bgModal} max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("project.select-license-modal-title")}</h2>
          </div>
        </div>

        {/* Search Section */}
        <div className="space-y-4 px-6 pb-4 flex-shrink-0">
          {/* Filter Inputs */}
          <div className="grid grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon
                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
              />
              <input
                type="text"
                placeholder={t("project.search-license-placeholder")}
                value={searchLicenseCriteria.licenseNameForSearch}
                onChange={(e) =>
                  setSearchLicenseCriteria((prev) => ({ ...prev, licenseNameForSearch: e.target.value }))
                }
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("project.advancedSearch")}</span>
              <span className="sm:hidden">Advanced</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 flex-shrink-0">
            {/* Arrow pointer chỉ về Advanced button */}
            <div
              className={`absolute -top-2 z-10 h-0 w-0`}
              style={{
                right: "calc(33.333% / 2 - 16px)", // Center of 3rd column minus half width of triangle
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid #374151`,
                filter: `drop-shadow(0 0 0 1px #292D33)`,
              }}
            ></div>

            <AdvancedSearch
              title={t("project.advancedSearch")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
            />
          </div>
        )}

        {/* Table Section - Scrollable */}
        <div className="flex flex-1 flex-col px-6 min-h-0 overflow-y-hidden">
          <TableWithPagination
            columns={licenseTableColumns}
            columnConfig={licenseTableColumnConfig}
            data={licenseList}
            currentPage={currentLicensePage}
            setCurrentPage={setCurrentLicensePage}
            loading={getLicenseListData.isPending || isSearching}
            rowKey="licenseIdentifier"
            totalPages={totalLicensePages}
            totalData={totalLicenseData}
            maxRowsPerPage={itemsPerPage}
            hasRadioButton={true}
            selectedRows={tempSelectedLicenseSeq}
            onCheckboxChange={handleOnCheckRadioChange}
            onRowClick={handleOnRowLicenseListClick}
            onItemsPerPageChange={handleItemsPerPageChangeWithReset}
            showPagination={true}
            scrollableBody={true}
            showItemsPerPageSelector={true}
          />
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              {/* Cancel Button */}
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSelect} disabled={!selectedLicenseItem}>
              {t("common.select-button")}
            </PrimaryButton>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseSelectionModal;
